/**
 * Schedule & business rule service.
 *
 * Centralizes all date/time business rules that were previously scattered
 * across ReservationForm.vue and FilterForm.vue.
 *
 * All functions are pure (stateless) and receive a ScheduleConfig object:
 * @typedef {Object} ScheduleConfig
 * @property {string}         timezone
 * @property {number}         regularOpeningTime
 * @property {number}         regularClosingTime
 * @property {number}         scheduleSundayEnd
 * @property {number|false}   extendedTo
 * @property {string}         tenant            - 'bike' or 'jaira'
 * @property {boolean}        blockPastReservation
 * @property {boolean}        preventNextMorningReservations
 * @property {moment.Moment}  serverTimeMoment
 */
import moment from 'moment-timezone';
import { toServerMoment } from './time-service';
import {
    PAST_BOOKING_BUFFER_HOURS,
    NEXT_MORNING_EARLIEST_HOUR,
    NEXT_MORNING_THRESHOLD_HOURS,
    SAME_DAY_END_HOUR_OFFSET,
} from '../constants/booking';

/**
 * Get the closing hour for a given date, accounting for Sunday and tenant rules.
 *
 * Replaces ReservationForm.getEndOfTheDay and FilterForm's inline Sunday logic.
 *
 * @param {Date} date
 * @param {ScheduleConfig} config
 * @returns {number}
 */
export function getEndOfDay(date, config) {
    if (config.tenant === 'jaira' && config.extendedTo) {
        return config.extendedTo;
    }
    var dayOfWeek = toServerMoment(date, config.timezone).day();
    if (dayOfWeek === 0) { // Sunday
        return config.scheduleSundayEnd;
    }
    return config.regularClosingTime;
}

/**
 * Get the earliest allowed pickup hour, applying next-morning prevention.
 *
 * When preventNextMorningReservations is true and the selected date's
 * opening time is less than 16 hours away, the earliest hour is 10 instead
 * of the regular opening time.
 *
 * Replaces ReservationForm.getEarliestHour and FilterForm.getEarliestHour.
 *
 * @param {Date} rentStartDate
 * @param {ScheduleConfig} config
 * @returns {number}
 */
export function getEarliestHour(rentStartDate, config) {
    var dayStart = toServerMoment(rentStartDate, config.timezone)
        .hours(config.regularOpeningTime)
        .minute(0)
        .second(0);

    if (
        config.preventNextMorningReservations &&
        config.serverTimeMoment.diff(dayStart, 'hours') > NEXT_MORNING_THRESHOLD_HOURS
    ) {
        return NEXT_MORNING_EARLIEST_HOUR;
    }
    return config.regularOpeningTime;
}

/**
 * Adjust 'from' hour to account for past-time blocking.
 *
 * When blockPastReservation is true and the date is today, the minimum
 * start hour is pushed to current_hour + PAST_BOOKING_BUFFER_HOURS.
 *
 * Replaces ReservationForm.checkIfStartIsInThePast and
 * FilterForm.checkifDateIsInThePast.
 *
 * @param {Date} date
 * @param {number} fromHour
 * @param {ScheduleConfig} config
 * @returns {number}
 */
export function applyPastTimeBuffer(date, fromHour, config) {
    var momentDate = toServerMoment(date, config.timezone);
    if (config.blockPastReservation && config.serverTimeMoment.isSame(momentDate, 'day')) {
        var minHour = config.serverTimeMoment.hour() + PAST_BOOKING_BUFFER_HOURS;
        if (fromHour < minHour) {
            return minHour;
        }
    }
    return fromHour;
}

/**
 * Check if today is too late for booking (within PAST_BOOKING_BUFFER_HOURS of closing).
 *
 * Used by ReservationForm.isDayDisabled to grey out today in the datepicker.
 *
 * @param {Date} date
 * @param {ScheduleConfig} config
 * @returns {boolean}
 */
export function isTooLateForToday(date, config) {
    var momentDate = toServerMoment(date, config.timezone);
    return (
        config.serverTimeMoment.isSame(momentDate, 'day') &&
        config.serverTimeMoment.hour() > (config.regularClosingTime - PAST_BOOKING_BUFFER_HOURS)
    );
}

/**
 * Detect if a given hour is after regular closing time.
 *
 * @param {number|string} hour
 * @param {number} closingTime
 * @returns {boolean}
 */
export function isAfterHours(hour, closingTime) {
    return parseInt(hour) > parseInt(closingTime);
}

/**
 * Compute time constraints for a given date based on existing reservations.
 * Returns { from, to } representing the allowed hour range.
 *
 * Replaces ReservationForm.getTimeConstraints (the most complex method).
 *
 * @param {Date} date
 * @param {Array} reservedDates - Array of { start, end } date strings
 * @param {ScheduleConfig} config
 * @returns {{ from: number, to: number }}
 */
export function getTimeConstraintsForDate(date, reservedDates, config) {
    var momentDate = toServerMoment(date, config.timezone);
    var from = getEarliestHour(date, config);
    var to = getEndOfDay(date, config);

    // If today and close to closing, cap extended hours
    if (
        config.serverTimeMoment.isSame(momentDate, 'day') &&
        config.serverTimeMoment.hour() > (config.regularClosingTime - PAST_BOOKING_BUFFER_HOURS)
    ) {
        to = Math.min(to, config.regularClosingTime);
    }

    // Narrow based on existing reservations
    for (var i = 0; i < reservedDates.length; i++) {
        var reserved = reservedDates[i];
        var reservedStart = moment.tz(reserved.start, config.timezone);
        var reservedEnd = moment.tz(reserved.end, config.timezone);

        if (reservedEnd.isSame(momentDate, 'day')) {
            var endHour = parseInt(reservedEnd.format('HH'));
            from = endHour > from ? endHour : from;
            if (parseInt(reservedEnd.format('mm')) === 30) {
                from++;
            }
        } else if (reservedStart.isSame(momentDate, 'day')) {
            var startHour = parseInt(reservedStart.format('HH'));
            to = startHour < to ? startHour : to;
        }
    }

    from = applyPastTimeBuffer(date, from, config);
    return { from: from, to: to };
}

/**
 * Get the minimum end hour when start and end are on the same day.
 *
 * @param {number|string} startHour
 * @returns {number}
 */
export function getSameDayEndHourMin(startHour) {
    return parseInt(startHour) + SAME_DAY_END_HOUR_OFFSET;
}

/**
 * Determine Sunday timepicker caps.
 *
 * Used by FilterForm to set timepicker `to` and `disabledto` bounds.
 * Returns { to, disabledTo } for the timepicker.
 *
 * @param {Date} date
 * @param {ScheduleConfig} config
 * @returns {{ to: number, disabledTo: number }}
 */
export function getSundayTimepickerCaps(date, config) {
    var isSunday = moment(date).isoWeekday() === 7;
    var hasDistinctExtended = config.extendedTo && config.extendedTo !== config.regularClosingTime;

    if (isSunday && !hasDistinctExtended) {
        return { to: config.scheduleSundayEnd, disabledTo: config.scheduleSundayEnd };
    }
    return {
        to: config.extendedTo || config.regularClosingTime,
        disabledTo: 24
    };
}

/**
 * Check if a day is blocked (all units occupied for the full working day).
 *
 * @param {Date} date
 * @param {Array} reservedDates - Array of { start, end } date strings
 * @param {ScheduleConfig} config
 * @returns {boolean}
 */
export function isDayBlocked(date, reservedDates, config) {
    var momentDate = toServerMoment(date, config.timezone);
    var startOfTheDay = momentDate.clone().hours(config.regularOpeningTime).minute(0).second(1);
    var endOfTheDay = momentDate.clone().hours(config.regularClosingTime).minute(0).second(0);

    for (var i = 0; i < reservedDates.length; i++) {
        var reserved = reservedDates[i];
        if (
            moment.tz(reserved.start, config.timezone).isBefore(startOfTheDay) &&
            moment.tz(reserved.end, config.timezone).isAfter(endOfTheDay)
        ) {
            return true;
        }
    }
    return false;
}

/**
 * Check if a date is a holiday.
 *
 * @param {Date} date
 * @param {Array<string>} holidays - Array of 'YYYY-MM-DD' strings
 * @param {string} timezone
 * @returns {boolean}
 */
export function isHoliday(date, holidays, timezone) {
    return holidays.includes(toServerMoment(date, timezone).format('YYYY-MM-DD'));
}

/**
 * Master predicate: check if a day should be disabled in the datepicker.
 * Combines blocked, holiday, and too-late-today checks.
 *
 * @param {Date} date
 * @param {Array} reservedDates
 * @param {Array<string>} holidays
 * @param {ScheduleConfig} config
 * @returns {boolean}
 */
export function isDayDisabled(date, reservedDates, holidays, config) {
    return (
        isDayBlocked(date, reservedDates, config) ||
        isHoliday(date, holidays, config.timezone) ||
        isTooLateForToday(date, config)
    );
}

/**
 * Compute disabled hours for today (FilterForm-specific).
 *
 * When the selected date is today, returns the minimum non-disabled hour
 * for start and end time pickers.
 *
 * @param {Date} startDate
 * @param {Date|null} endDate
 * @param {ScheduleConfig} config
 * @returns {{ startDisabledFrom: number, endDisabledFrom: number }}
 */
export function getDisabledHoursForToday(startDate, endDate, config) {
    var result = { startDisabledFrom: 0, endDisabledFrom: 0 };

    if (startDate && config.serverTimeMoment.isSame(toServerMoment(startDate, config.timezone), 'day')) {
        result.startDisabledFrom = config.serverTimeMoment.hour() + PAST_BOOKING_BUFFER_HOURS;
        if (endDate && config.serverTimeMoment.isSame(toServerMoment(endDate, config.timezone), 'day')) {
            result.endDisabledFrom = config.serverTimeMoment.hour() + PAST_BOOKING_BUFFER_HOURS + 1;
        }
    }

    return result;
}
